import * as React from 'react';
import { TextInput, VisuallyHidden } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { MultiValuePicker } from './value-pills.component';
import type { CatalogOption } from './source-resolution';
import type { OperatorInputs as OperatorInputsValue } from './types';

interface OperatorInputsProps {
  inputs: OperatorInputsValue | Record<string, never>;
  onChange: (next: OperatorInputsValue) => void;
  /**
   * Comparison-value options for the active condition, pre-resolved by
   * the parent off the enclosing item via the source's `valueCatalog`.
   */
  options: CatalogOption[];
  editPath: string;
  enabled: boolean;
  /**
   * Input shape of the active operator: `set` renders the multi-value chip
   * picker; `scalar` renders a single numeric input (the comparison operators).
   */
  inputShape: 'set' | 'scalar';
}

/**
 * Renders the value input for a condition operator. Set-membership operators
 * (`includes` / `not_includes`) take a set of codes → the multi-add picker;
 * numeric comparison operators take a single value → a numeric input.
 */
export const OperatorInputs: React.FC<OperatorInputsProps> = ({
  inputs: inputsProp,
  onChange,
  options,
  editPath,
  enabled,
  inputShape,
}) => {
  const { formatMessage } = useIntl();
  // Defensive: stored conditions may have `inputs: undefined` if the
  // framework's serializer dropped a fully-undefined object. Treat as
  // empty so the reads below don't throw.
  const inputs = inputsProp ?? {};

  if (inputShape === 'scalar') {
    const current =
      'value' in inputs && typeof inputs.value === 'string' ? inputs.value : '';
    const fieldId = `${editPath}.inputs.value`;
    return (
      <>
        {/* Scalar operators render outside a FormField, so give the numeric
            input an accessible name the same way the set picker does. */}
        <VisuallyHidden as="label" htmlFor={fieldId}>
          {formatMessage({ id: 'CONDITIONAL_ACTION_COMPARISON_VALUE' })}
        </VisuallyHidden>
        <TextInput
          id={fieldId}
          type="number"
          value={current}
          onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ value: evt.currentTarget.value })
          }
          readOnly={!enabled}
          css={{ backgroundColor: '$inputBackgroundDefault' }}
        />
      </>
    );
  }

  // Narrow with `in` so a non-array shape (empty or the single-value variant)
  // reads as an empty selection without an `as` cast.
  const initialValues =
    'values' in inputs && Array.isArray(inputs.values) ? inputs.values : [];
  return (
    <MultiValuePicker
      value={initialValues}
      onChange={(next) => onChange({ values: next })}
      options={options}
      editPath={editPath}
      enabled={enabled}
    />
  );
};
